import React, { useState, useMemo, useRef, useEffect } from "react";
import { Modal, Button, Form, Spinner, Table } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import toast from "react-hot-toast";

/* ======================================================
   EDUALL PRICING (ARRAY ORIGINAL COMPLETO)
====================================================== */

const EDUALL_PRICING = {
  currency: "Kz",
  ivaIncluded: false,

  modules: [
    "Secretaria","Biblioteca","Chat / Gestor de tarefa",
    "Administração","Finanças","Transporte escolar",
    "Portal do Professor","Portal do Aluno",
    "Portal do Encarregado","Pedagogia",
    "Monitoramento / CCTV","Sistemas ERP"
  ],

  categories: {
    universities: {
      name: "Universidades e Institutos Superiores",
      billingOptions: ["trimestral","semestral","anual"],
      plans: [
        { id:"uni_platina",name:"Plano Platina",price:399999,billing:"mensal",studentLimit:4000,includedModules:"todos" },
        { id:"uni_diamante",name:"Plano Diamante",price:469999,billing:"mensal",studentLimit:10000,includedModules:"todos" }
      ]
    },

    publicSchools: {
      name:"Escolas Públicas",
      billingOptions:["trimestral","semestral","anual"],
      plans:[
        { id:"pub_basico",name:"Básico",price:66999,billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar"] },
        { id:"pub_premium",name:"Premium",price:76999,billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia","Monitoramento / CCTV"] },
        { id:"pub_moderado",name:"Moderado",price:2499,billing:"anual_por_aluno",minimumStudents:350,includedModules:"todos" }
      ]
    },

    privateSchools:{
      name:"Colégios & Escolas Privadas ",
      plans:[
        { id:"priv_basico",name:"Básico",price:99999,billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar"] },
        { id:"priv_premium",name:"Premium",price:169999,billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia"] },
        { id:"priv_avancado",name:"Avançado",price:249999,billing:"mensal",includedModules:"todos" }
      ]
    }
  },

  additionalServices:[
    { id:"sms",name:"SMS Institucional",price:13.99,billing:"por_sms" },
    { id:"pagamentos_online",name:"Pagamentos Online",price:79999,billing:"mensal" },
    { id:"manutencao_anual",name:"Manutenção / Formação / Suporte Anual",price:39999,billing:"anual_unico" },
    { id:"leitor_rfid",name:"Leitor RFID Portátil",price:25999,billing:"pagamento_unico" },
    { id:"terminal_rfid",name:"Terminal RFID Standalone",price:99840,billing:"pagamento_unico" }
  ]
};

const BILLING_MULTIPLIER = {
  trimestral:3,
  semestral:6,
  anual:12
};

/* ======================================================
   UI STYLE (MANTIDO)
====================================================== */

const styles = `
.pretty-modal .modal-dialog{ max-width:900px; }
.pretty-modal .modal-content{
  border-radius:18px;
  border:1px solid rgba(226,232,240,.95);
  box-shadow:0 30px 90px rgba(2,6,23,.22);
}
.pretty-modal .modal-body{
  background:linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%);
  max-height:500px;
  overflow-y:auto;
}
.cardlet{
  background:#fff;
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px;
  padding:16px;
  margin-bottom:16px;
}
.cardlet-title{
  font-size:.85rem;
  font-weight:700;
  color:#475569;
  margin-bottom:10px;
}
.price-box{
  border-radius:16px;
  border:1px solid rgba(226,232,240,.95);
  padding:20px;
  background:#fff;
}
.price-total{
  font-size:1.4rem;
  font-weight:800;
}
.actions{
  display:flex;
  justify-content:flex-end;
  gap:10px;
}
.btn-primary{ border-radius:999px !important; }
.btn-soft{ border-radius:999px !important; }
`;

function generateProposalCode(){
  const rand=Math.floor(100000000+Math.random()*900000000);
  return `EDPR-${rand}#`;
}

export default function SchoolProposalModal({ toggle_btn }) {

  const [show,setShow]=useState(false);
  const [loading,setLoading]=useState(false);

  const [proposalCode,setProposalCode]=useState("");
  const [proposalName,setProposalName]=useState("");
  const [clientName,setClientName]=useState("");
  const [institutionName,setInstitutionName]=useState("");
  const [institutionNif,setInstitutionNif]=useState("");

  const [categoryKey,setCategoryKey]=useState("publicSchools");
  const [planId,setPlanId]=useState("");
  const [billing,setBilling]=useState("anual");
  const [students,setStudents]=useState(0);
  const [smsQuantity,setSmsQuantity]=useState(1000);
  const [selectedServices,setSelectedServices]=useState([]);
  const [discountPercent,setDiscountPercent]=useState(0);

  const pdfRef=useRef();

  useEffect(()=>{
    if(show) setProposalCode(generateProposalCode());
  },[show]);

  const category=EDUALL_PRICING.categories[categoryKey];
  const plan=category?.plans.find(p=>p.id===planId);
  const multiplier=BILLING_MULTIPLIER[billing];

  const calculation=useMemo(()=>{

    let base=0;

    if(plan){
      if(plan.billing==="anual_por_aluno"){
        base=plan.price*students;
      }else{
        base=plan.price*multiplier;
      }
    }

    let servicesTotal=0;

    selectedServices.forEach(id=>{
      const service=EDUALL_PRICING.additionalServices.find(s=>s.id===id);
      if(!service)return;

      switch(service.billing){
        case "mensal": servicesTotal+=service.price*multiplier; break;
        case "por_sms": servicesTotal+=service.price*smsQuantity; break;
        default: servicesTotal+=service.price; break;
      }
    });

    const subtotal=base+servicesTotal;
    const discountValue=(subtotal*discountPercent)/100;
    const total=subtotal-discountValue;

    return {base,servicesTotal,subtotal,discountValue,total};

  },[plan,students,multiplier,selectedServices,smsQuantity,discountPercent]);

  /* ======================================================
     PDF COMPLETO
  ====================================================== */
    const buildPDF = () => {

      const coverUrl = "https://ik.imagekit.io/zks5iegia/1.png";
      const footerUrl = "https://ik.imagekit.io/zks5iegia/9.png";

      const today = new Date().toLocaleDateString("pt-PT");

      const modulesIncluded =
        plan?.includedModules === "todos"
          ? EDUALL_PRICING.modules
          : plan?.includedModules || [];

      const modulesInline = modulesIncluded
        .map((m, i) => `${i + 1} - ${m}`)
        .join("; ");

      /* ==========================
        SERVIÇOS ADICIONAIS
      ========================== */

      const servicesRows = selectedServices.map(id => {
        const s = EDUALL_PRICING.additionalServices.find(x => x.id === id);
        if (!s) return "";

        let value = 0;

        switch (s.billing) {
          case "mensal":
            value = s.price * multiplier;
            break;
          case "por_sms":
            value = s.price * smsQuantity;
            break;
          default:
            value = s.price;
            break;
        }

        return `
          <div class="service-row">
            <span>${s.name}</span>
            <span class="price">${value.toLocaleString()} Kz</span>
          </div>
        `;
      }).join("");

      return `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');

        *{
          margin:0;
          padding:0;
          box-sizing:border-box;
        }

        body{
          font-family:'Montserrat', sans-serif;
          font-size:11.5px;
          color:#000;
        }

        #pdf-root{
          width:794px;
        }

        .cover{
          width:794px;
          height:1123px;
          background:url('${coverUrl}') center/cover no-repeat;
        }

        @page {
          margin: 25mm 22mm 25mm 22mm;
        }

        .content{
          width:794px;
          padding:60px 80px 60px 80px;
        }

        .title{
          font-size:23px;
          font-weight:700;
          color:#6051ff;
          margin-bottom:12px;
        }

        .proposal-date{
          font-size:12px;
          font-weight:600;
          margin-bottom:30px;
        }

        .section{
          margin-top:28px;
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .section-title{
          font-size:14px;
          font-weight:700;
          color:#6051ff;
          margin-bottom:14px;
        }

        .grid{
          display:grid;
          grid-template-columns:1fr 1fr;
          gap:20px 45px;
        }

        .label{
          font-size:10.5px;
          font-weight:600;
          color:#555;
          margin-bottom:4px;
          display:block;
        }

        .plan-box{
          padding:15px;
          background:#f5f6ff;
          border-left:4px solid #6051ff;
          border-radius:6px;
          font-size:12px;
          font-weight:600;
        }

        .modules-inline{
          font-size:12px;
          font-weight:600;
          line-height:1.8;
          word-wrap:break-word;
        }

        .service-row{
          display:flex;
          justify-content:space-between;
          padding:11px 0;
          border-bottom:1px solid #ddd;
        }

        .price{
          font-weight:700;
        }

        .simple-summary{
          margin-top:25px;
          padding-top:15px;
          border-top:2px solid #eee;
        }

        .summary-line{
          display:flex;
          justify-content:space-between;
          margin-bottom:8px;
          font-weight:600;
        }

        .summary-total{
          display:flex;
          justify-content:space-between;
          margin-top:12px;
          padding:12px 16px;
          background:#6051ff;
          color:#fff;
          font-size:15px;
          font-weight:700;
          border-radius:6px;
        }

        .footer{
          width:794px;
          height:1123px;
          background:url('${footerUrl}') center/cover no-repeat;
          page-break-before: always;
          page-break-inside: avoid;
          break-inside: avoid;
          display:block;
          overflow:hidden;
        }

      </style>

      <div id="pdf-root">

        <!-- CAPA -->
        <div class="cover"></div>

        <!-- CONTEÚDO -->
        <div class="content">

          <div class="title">Proposta Comercial</div>

          <div class="proposal-date">
            Data da Proposta: <strong>${today}</strong>
          </div>

          <!-- IDENTIFICAÇÃO -->
          <div class="section">
            <div class="section-title">Identificação</div>
            <div class="grid">
              <div><span class="label">Código</span>${proposalCode}</div>
              <div><span class="label">Proposta</span>${proposalName}</div>
              <div><span class="label">Cliente</span>${clientName}</div>
              <div><span class="label">Instituição</span>${institutionName}</div>
              <div><span class="label">NIF</span>${institutionNif}</div>
              <div><span class="label">Categoria</span>${category?.name}</div>
            </div>
          </div>

          <!-- PLANO -->
          <div class="section">
            <div class="section-title">Plano Selecionado</div>

            <div class="plan-box">
              ${plan?.name || "-"} — ${billing}

              ${
                plan?.billing === "anual_por_aluno"
                  ? `
                    <div style="margin-top:10px;font-weight:500;">
                      Valor Unitário por Estudante:
                      <strong>${plan.price.toLocaleString()} Kz</strong>
                    </div>

                    <div style="margin-top:6px;font-weight:500;">
                      Quantidade de Estudantes Selecionados:
                      <strong>${students}</strong>
                    </div>

                    <div style="margin-top:6px;font-weight:600;">
                      Total do Plano:
                      <strong>
                        ${plan.price.toLocaleString()} Kz × ${students}
                        = ${calculation.base.toLocaleString()} Kz
                      </strong>
                    </div>
                  `
                  : `
                    <div style="margin-top:10px;font-weight:600;">
                      Valor do Plano:
                      ${calculation.base.toLocaleString()} Kz
                    </div>
                  `
              }

            </div>
          </div>

          <!-- MÓDULOS -->
          <div class="section">
            <div class="section-title">Módulos Inclusos</div>
            <div class="modules-inline">
              ${modulesInline}
            </div>
          </div>

          <!-- SERVIÇOS + RESUMO -->
          <div class="section">
            ${
              selectedServices.length
                ? `
                  <div class="section-title">Serviços Adicionais</div>
                  ${servicesRows}
                `
                : ""
            }

            <div class="simple-summary">

              ${
                plan?.billing === "anual_por_aluno"
                  ? `
                    <div class="summary-line">
                      <span>Plano (${plan.price.toLocaleString()} Kz × ${students} estudantes)</span>
                      <span>${calculation.base.toLocaleString()} Kz</span>
                    </div>
                  `
                  : `
                    <div class="summary-line">
                      <span>Plano</span>
                      <span>${calculation.base.toLocaleString()} Kz</span>
                    </div>
                  `
              }

              ${
                selectedServices.length
                  ? `
                    <div class="summary-line">
                      <span>Serviços Adicionais</span>
                      <span>${calculation.servicesTotal.toLocaleString()} Kz</span>
                    </div>
                  `
                  : ""
              }

              <div class="summary-line">
                <span>Subtotal</span>
                <span>${calculation.subtotal.toLocaleString()} Kz</span>
              </div>

              <div class="summary-line">
                <span>Desconto (${discountPercent}%)</span>
                <span>- ${calculation.discountValue.toLocaleString()} Kz</span>
              </div>

              <div class="summary-total">
                <span>Total Geral</span>
                <span>${calculation.total.toLocaleString()} Kz</span>
              </div>

            </div>
          </div>

        </div>

        <!-- FOOTER -->
        <div class="footer"></div>

      </div>
      `;
    };

  const exportPDF=async()=>{

    if(!proposalName||!clientName||!institutionName){
      toast.error("Preencha os dados principais.");
      return;
    }

    try{
      setLoading(true);

      const container=document.createElement("div");
      container.innerHTML=buildPDF();
      document.body.appendChild(container);
await html2pdf()
  .from(document.getElementById("pdf-root"))
  .set({
    margin:0,
    filename:`${proposalCode}.pdf`,
    html2canvas:{ scale:2, useCORS:true },
    jsPDF:{ unit:"mm", format:"a4" },
    pagebreak:{ mode:['css'] }
  })
  .save();
      document.body.removeChild(container);

      toast.success("PDF completo gerado com sucesso!");

    }catch{
      toast.error("Erro ao gerar PDF");
    }finally{
      setLoading(false);
    }
  };

  return(
    <>
      <style>{styles}</style>
      <div onClick={()=>setShow(true)} style={{display:"inline-block"}}>
        {toggle_btn || <Button>Nova Proposta</Button>}
      </div> 

      <Modal show={show} onHide={()=>!loading&&setShow(false)} centered className="pretty-modal">
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Calculadora Comercial EDUALL</Modal.Title>
        </Modal.Header> 

        <Modal.Body>

          {/* DADOS COMERCIAIS */}
          <div className="cardlet">
            <div className="cardlet-title">Dados da Proposta</div>

            <Form.Control className="mb-2" value={proposalCode} disabled />
            <Form.Control className="mb-2" placeholder="Nome da Proposta" value={proposalName} onChange={e=>setProposalName(e.target.value)} />
            <Form.Control className="mb-2" placeholder="Nome do Cliente" value={clientName} onChange={e=>setClientName(e.target.value)} />
            <Form.Control className="mb-2" placeholder="Nome da Instituição" value={institutionName} onChange={e=>setInstitutionName(e.target.value)} />
            <Form.Control placeholder="NIF da Instituição" value={institutionNif} onChange={e=>setInstitutionNif(e.target.value)} />
          </div>

          {/* RESTANTE DESIGN ORIGINAL MANTIDO */}

          <div className="cardlet">
            <div className="cardlet-title">Plano e Faturação</div>

            <Form.Select value={categoryKey} onChange={e=>{setCategoryKey(e.target.value);setPlanId("");}} className="mb-2">
              {Object.keys(EDUALL_PRICING.categories).map(key=>(
                <option key={key} value={key}>{EDUALL_PRICING.categories[key].name}</option>
              ))}
            </Form.Select>

            <Form.Select value={planId} onChange={e=>setPlanId(e.target.value)} className="mb-2">
              <option value="">Selecionar Plano</option>
              {category?.plans.map(p=>(
                <option key={p.id} value={p.id}>
                  {p.name} — {p.price.toLocaleString()} Kz
                </option>
              ))}
            </Form.Select>

            <Form.Select value={billing} onChange={e=>setBilling(e.target.value)}>
              <option value="trimestral">Trimestral</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
            </Form.Select>
          </div>

          {plan?.billing==="anual_por_aluno"&&(
            <div className="cardlet">
              <div className="cardlet-title">Número de Estudantes</div>
              <Form.Control type="number" value={students} onChange={e=>setStudents(Number(e.target.value))}/>
            </div>
          )}

          <div className="cardlet">
            <div className="cardlet-title">Serviços Adicionais</div>

            {EDUALL_PRICING.additionalServices.map(service=>(
              <div key={service.id} className="mb-2">
                <Form.Check
                  type="switch"
                  label={`${service.name} - ${service.price.toLocaleString()} Kz`}
                  checked={selectedServices.includes(service.id)}
                  onChange={e=>{
                    if(e.target.checked)
                      setSelectedServices([...selectedServices,service.id]);
                    else
                      setSelectedServices(selectedServices.filter(id=>id!==service.id));
                  }}
                />

                {service.billing==="por_sms"&&selectedServices.includes(service.id)&&(
                  <Form.Control type="number" value={smsQuantity} onChange={e=>setSmsQuantity(Number(e.target.value))}/>
                )}
              </div>
            ))}
          </div>

          <div className="cardlet">
            <div className="cardlet-title">Desconto Comercial (%)</div>
            <Form.Control type="number" value={discountPercent} onChange={e=>setDiscountPercent(Number(e.target.value))}/>
          </div>

          <div ref={pdfRef} className="price-box">
            <Table bordered>
              <tbody>
                <tr><td>Licenciamento</td><td>{calculation.base.toLocaleString()} Kz</td></tr>
                <tr><td>Serviços</td><td>{calculation.servicesTotal.toLocaleString()} Kz</td></tr>
                <tr><td>Subtotal</td><td>{calculation.subtotal.toLocaleString()} Kz</td></tr>
                <tr><td>Desconto</td><td>- {calculation.discountValue.toLocaleString()} Kz</td></tr>
                <tr className="price-total">
                  <td>Total Geral</td>
                  <td>{calculation.total.toLocaleString()} Kz</td>
                </tr>
              </tbody>
            </Table>
          </div>

        </Modal.Body>

        <Modal.Footer>
          <div className="actions" style={{width:"100%"}}>
            <Button variant="outline-secondary" className="btn-soft" disabled={loading} onClick={()=>setShow(false)}>
              Fechar
            </Button>

            <Button variant="primary" disabled={loading} onClick={exportPDF}>
              {loading ? <Spinner size="sm" animation="border"/> : "Gerar PDF"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}